import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { ListFilter, Search as SearchIcon, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import InputCst from "@components/InputCst/InputCst";
// import SelectCst from "@components/InputCst/SelectCst";
import ListItemCardCst from "@components/ListItemCardCst";
import PaginatorCst from "@components/PaginatorCst";
import SortMenuCst from "@components/SortMenuCst";
// import { ACTIVITY_TYPES } from "@pages/Contents/V2/Corporate/Catalog/AddActivity/config/activityTypes";
import { mapEntityToCard } from "@pages/Contents/V2/Corporate/Catalog/mapEntityToCard";
import { useLOSearch } from "@pages/Contents/V2/Corporate/Catalog/search/useLOSearch";
import { useLOFilterableFields } from "@pages/Contents/V2/Corporate/Catalog/search/useLOFilterableFields";
import SearchFiltersAccordion from "@pages/Contents/V2/Corporate/Catalog/search/SearchFiltersAccordion";
import {
  buildSearchConditions,
  LO_SORT_OPTION_KEYS,
  LO_SORT_ORDER_BY,
  SEARCH_FILTER_ORDER_BY_TAB,
} from "@pages/Contents/V2/Corporate/Catalog/search/types";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";

const PAGE_SIZE = 16;

export type LOPickerEntityType = "activity" | "course";

export interface LOPickerSelectedItem {
  id: number;
  title: string;
  type: string;
  entity: Record<string, any>;
}

interface LOPickerCstProps {
  open: boolean;
  onClose: () => void;
  title: string;
  entityType: LOPickerEntityType;
  excludeIds: number[];
  initialSelectedItems?: LOPickerSelectedItem[];
  contentBeforeSearch?: React.ReactNode;
  maxSelection?: number;
  validateConfirm?: () => boolean;
  onConfirm: (added: LOPickerSelectedItem[]) => void;
  onCreateNew?: () => void;
}

const LOPickerCst: React.FC<LOPickerCstProps> = ({
  open,
  onClose,
  title,
  entityType,
  excludeIds,
  initialSelectedItems = [],
  contentBeforeSearch,
  maxSelection,
  validateConfirm,
  onConfirm,
  onCreateNew,
}) => {
  const { t } = useTranslation("lo-picker");
  const { t: tCatalog } = useTranslation("catalog-v2");
  // const { t: tActivity } = useTranslation("wizard-activity");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [searchSubmitted, setSearchSubmitted] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [accordionFilters, setAccordionFilters] = useState<Record<string, any>>(
    {},
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState("id-desc");

  const [selectedMap, setSelectedMap] = useState<Map<number, any>>(new Map());

  const hasActiveFilters = useMemo(() => {
    const hasValue = (val: any): boolean => {
      if (val === undefined || val === null || val === "") return false;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === "object") return Object.values(val).some(hasValue);
      return true;
    };
    return Object.values(accordionFilters || {}).some(hasValue);
  }, [accordionFilters]);

  const filtersHighlighted = hasActiveFilters || filtersOpen;

  const sortOptions = useMemo(
    () =>
      LO_SORT_OPTION_KEYS.map((key) => ({
        value: key,
        label: String(tCatalog(`sort.${key}`)),
      })),
    [tCatalog],
  );

  const { fields: filterableFields } = useLOFilterableFields(corporateId, open);

  const conditions = useMemo(
    () =>
      buildSearchConditions({
        contentTypes: [
          entityType === "course" ? "COURSE" : "DIDACTICAL_ACTIVITY",
        ],
        includeDrafts: false,
        fields: filterableFields,
        filterValues: {
          ...accordionFilters,
          ...(typeFilter && entityType === "activity"
            ? { activityType: [typeFilter] }
            : {}),
        },
      }),
    [entityType, filterableFields, accordionFilters, typeFilter],
  );

  const {
    items: rawItems,
    totalItems,
    totalPages,
    isLoading,
  } = useLOSearch({
    corporateId,
    conditions,
    searchText: searchSubmitted,
    orderBy: LO_SORT_ORDER_BY[sort] ?? LO_SORT_ORDER_BY["id-desc"],
    page,
    pageSize,
    enabled: open && !!corporateId,
  });

  const items = useMemo(
    () => (rawItems || []).map((raw: any) => raw.lo),
    [rawItems],
  );

  useEffect(() => {
    if (!open) return;
    setSelectedMap(
      new Map(
        initialSelectedItems.map((item) => [
          item.id,
          {
            ...item.entity,
            id: item.id,
            title: item.title,
            type: item.type,
          },
        ]),
      ),
    );
    setPage(1);
    setSearchInput("");
    setSearchSubmitted("");
    setTypeFilter("");
    setAccordionFilters({});
    setFiltersOpen(false);
    setSort("id-desc");
  }, [open]);

  const visibleItems = useMemo(() => {
    if (!excludeIds || excludeIds.length === 0) return items;
    const exclude = new Set(excludeIds);
    return items.filter((it) => !exclude.has(Number(it.id)));
  }, [items, excludeIds]);

  const handleSubmitSearch = () => {
    setSearchSubmitted(searchInput.trim());
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmitSearch();
    }
  };

  const handleSelectToggle = (entity: any, next: boolean) => {
    setSelectedMap((prev) => {
      const copy = new Map(prev);
      const id = Number(entity.id);
      if (next) {
        if (
          !copy.has(id) &&
          maxSelection != null &&
          copy.size >= maxSelection
        ) {
          return prev;
        }
        copy.set(id, entity);
      } else {
        copy.delete(id);
      }
      return copy;
    });
  };

  const handleConfirm = () => {
    if (validateConfirm && !validateConfirm()) return;
    const added: LOPickerSelectedItem[] = Array.from(selectedMap.entries()).map(
      ([id, entity]) => ({
        id,
        title: entity.title || entity.name || `#${id}`,
        type: entity.type || entity.learning_object_type || "",
        entity,
      }),
    );
    onConfirm(added);
    onClose();
  };

  // TODO: opzioni del quick filter tipo attività, commentate insieme alla
  // select esterna all'accordion.
  // const typeOptions = useMemo(
  //   () => [
  //     { value: "", label: t("type.all") },
  //     ...ACTIVITY_TYPES.map((td) => ({
  //       value: td.key,
  //       label: tActivity(td.titleI18nKey, { defaultValue: td.key }) || td.key,
  //     })),
  //   ],
  //   [t, tActivity],
  // );

  const canConfirm = selectedMap.size > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "12px 24px 16px",
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
          pt: 20,
        }}
      >
        {contentBeforeSearch}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
            mt: "10px",
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              flex: "1 1 240px",
              minWidth: 0,
              "& .MuiFormHelperText-root": { display: "none" },
            }}
            onKeyDown={handleSearchKeyDown}
          >
            <InputCst
              id="lo-picker-search"
              placeholder={t("search-placeholder") || ""}
              inputSearch
              value={searchInput}
              onChange={(e: any) => setSearchInput(e.target.value)}
              startAdornment={
                <SearchIcon
                  size={16}
                  color={theme.palette.systemSecondary01}
                  style={{ marginRight: 8 }}
                />
              }
            />
          </Box>
          {/* TODO: quick filter tipo attività rimosso perché duplicato del
              campo "Tipo attività" nell'accordion dinamico ripristinare se
              servirà di nuovo la scorciatoia fuori dall'accordion.
          {entityType === "activity" && (
            <Box sx={{ flex: "0 0 180px" }}>
              <SelectCst
                id="lo-picker-type"
                label={t("type.label") || ""}
                value={typeFilter}
                onChange={(v: any) => {
                  setTypeFilter(String(v || ""));
                  setPage(1);
                }}
                options={typeOptions}
              />
            </Box>
          )}
          */}
          {/* pulsante "Crea" nascosto nel picker
          {onCreateNew && (
            <Button
              onClick={onCreateNew}
              variant="outlined"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "8px",
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
                flexShrink: 0,
              }}
            >
              {t("create-new")}
            </Button>
          )}
          */}
          <ButtonSecondary
            size="md"
            startIcon={<ListFilter size={16} />}
            onClick={() => setFiltersOpen((prev) => !prev)}
            sxButton={
              filtersHighlighted
                ? {
                    borderColor: theme.palette.systemSecondary01,
                  }
                : undefined
            }
          >
            {tCatalog("filters-button")}
          </ButtonSecondary>
        </Box>

        <SearchFiltersAccordion
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          fields={filterableFields}
          fieldOrder={
            SEARCH_FILTER_ORDER_BY_TAB[
              entityType === "course" ? "courses" : "activities"
            ]
          }
          value={accordionFilters}
          onApply={(next) => {
            setAccordionFilters(next);
            setPage(1);
          }}
          columns={3}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography
            sx={{ fontSize: "13px", color: theme.palette.text.primary }}
          >
            {t("selected-count", { count: selectedMap.size })}
          </Typography>
          <SortMenuCst
            id="lo-picker-sort"
            value={sort}
            options={sortOptions}
            onChange={(next) => {
              setSort(next || "id-desc");
              setPage(1);
            }}
          />
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : visibleItems.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 6,
              color: theme.palette.text.primary,
              fontSize: "13px",
            }}
          >
            {t("empty")}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {visibleItems.map((entity) => {
              const id = Number(entity.id);
              const selected = selectedMap.has(id);
              const cardProps = mapEntityToCard(entity, entityType, {
                t: tCatalog,
                showAlternativeSectionCount: true,
                onDetails: () => {
                  // TODO: pagina dettaglio v2 non disponibile oggi.
                  console.info("[lo-picker] details — TODO", entityType, id);
                },
              });
              return (
                <ListItemCardCst
                  key={id}
                  {...cardProps}
                  // pulsante "Dettagli" nascosto nel picker
                  actions={[]}
                  selectable
                  selected={selected}
                  onSelect={(next) => handleSelectToggle(entity, next)}
                />
              );
            })}
          </Box>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <PaginatorCst
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setPage}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(1);
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px 20px", gap: 2 }}>
        <Box sx={{ flex: 1 }} />
        <ButtonPrimary size="md" onClick={handleConfirm} disabled={!canConfirm}>
          {t("confirm")}
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  );
};

export default LOPickerCst;
